import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend_url } from "../App";
import { menuItems } from "../Constants/menuItems";
import "./CSS/ExcelUpload.css";

const COL_STOCK_NO    = "stock no";
const COL_RETAIL      = "retail price";
const COL_CLOSING_QTY = "closing bal.qty";

const KNOWN_CATEGORY_VALUES = new Set(menuItems.map((m) => m.value?.toLowerCase()));

const isKnownCategory = (cat) =>
  !cat || KNOWN_CATEGORY_VALUES.has(cat?.toLowerCase());

const getCatLabel = (cat) =>
  menuItems.find((m) => m.value?.toLowerCase() === cat?.toLowerCase())?.label || null;

const CATEGORY_TABS = [
  { label: "All", value: "all" },
  ...menuItems.map((m) => ({ label: m.label, value: m.value })),
  { label: "Unknown", value: "__unknown__" },
];

const normalizeKey = (k) => k.trim().toLowerCase();

const getAutoStatus = (qty) => {
  const n = Number(qty);
  if (isNaN(n) || n < 6) return "Out of Stock";
  if (n < 15)            return "Low Stock";
  return "In-Stock";
};

const isDiff = (a, b) => {
  const na = Number(a), nb = Number(b);
  if (!isNaN(na) && !isNaN(nb) && a !== "" && b !== "") return na !== nb;
  return String(a).trim() !== String(b).trim();
};

const fmtPrice = (v) =>
  v !== "" && v !== undefined && v !== null
    ? `₹${Number(v).toLocaleString("en-IN")}`
    : "—";

const ExcelUpload = () => {
  const [rows, setRows]             = useState([]);
  const [error, setError]           = useState("");
  const [fileName, setFileName]     = useState("");
  const [dragging, setDragging]     = useState(false);
  const [search, setSearch]         = useState("");
  const [saving, setSaving]         = useState(false);
  const [saveStatus, setSaveStatus] = useState({});
  const [showChangedOnly, setShowChangedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | matched | changed | nochange | notfound
  const [activeCategory, setActiveCategory]   = useState("all");
  const inputRef = useRef();

  /* ── fetch & enrich ── */
  const enrichWithDb = async (parsed) => {
    try {
      const res  = await fetch(`${backend_url}/allproducts`);
      const data = await res.json();

      /* group ALL products by unique_id — one id can have multiple products */
      const byId = {};
      data.forEach((p) => {
        const key = p.unique_id?.trim().toLowerCase();
        if (!byId[key]) byId[key] = [];
        byId[key].push(p);
      });

      return parsed.map((row) => {
        const key     = String(row.stockNo).trim().toLowerCase();
        const matches = byId[key] || [];
        const primary = matches[0] || null;
        return {
          ...row,
          dbProduct:    primary,
          dbMatches:    matches,
          priceChanged: primary ? isDiff(row.retailPrice, primary.old_price) : false,
          qtyChanged:   primary ? isDiff(row.closingQty,  primary.quantity)  : false,
        };
      });
    } catch {
      toast.error("Could not fetch products from server.");
      return parsed.map((row) => ({ ...row, dbProduct: null, dbMatches: [], priceChanged: false, qtyChanged: false }));
    }
  };

  /* ── parse file ── */
  const parseFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setError("Please upload a valid Excel (.xlsx, .xls) or CSV file.");
      return;
    }
    setError("");
    setFileName(file.name);
    setRows([]);
    setSaveStatus({});
    setActiveCategory("all");

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data     = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet    = workbook.Sheets[workbook.SheetNames[0]];
      const json     = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!json.length) { setError("The file is empty."); return; }

      const rawKeys = Object.keys(json[0]);
      const keyMap  = {};
      rawKeys.forEach((rk) => { keyMap[normalizeKey(rk)] = rk; });

      const required = [COL_STOCK_NO, COL_RETAIL, COL_CLOSING_QTY];
      const missing  = required.filter((k) => !keyMap[k]);
      if (missing.length) {
        setError(`Missing columns: "${missing.join('", "')}". Found: ${rawKeys.join(", ")}`);
        return;
      }

      const parsed = json.map((row, idx) => ({
        _idx:        idx,
        stockNo:     row[keyMap[COL_STOCK_NO]],
        retailPrice: row[keyMap[COL_RETAIL]],
        closingQty:  row[keyMap[COL_CLOSING_QTY]],
        dbProduct:   null,
        priceChanged: false,
        qtyChanged:   false,
      }));

      const enriched = await enrichWithDb(parsed);
      setRows(enriched);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFile = (e) => parseFile(e.target.files[0]);
  const handleDrop = (e) => { e.preventDefault(); setDragging(false); parseFile(e.dataTransfer.files[0]); };

  const handleClear = () => {
    setRows([]); setFileName(""); setError("");
    setSearch(""); setSaveStatus({}); setActiveCategory("all"); setStatusFilter("all");
    if (inputRef.current) inputRef.current.value = "";
  };

  /* ── Bulk save ── */
  /* ── Build payload — only send fields that actually changed ── */
  const buildPayload = (row) => {
    const payload = {};
    if (row.qtyChanged) {
      payload.quantity = row.closingQty;
      payload.status   = getAutoStatus(row.closingQty);
    }
    if (row.priceChanged) {
      payload.old_price = row.retailPrice;
    }
    return payload;
  };

  /* patch every product sharing this stock no */
  const patchAllMatches = async (row) => {
    const targets = row.dbMatches?.length ? row.dbMatches : [row.dbProduct];
    const payload = buildPayload(row);
    const results = await Promise.all(
      targets.map((p) =>
        fetch(`${backend_url}/updatestock/${p._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).then((r) => r.json()).catch(() => ({ success: false }))
      )
    );
    return results.every((r) => r.success) ? "ok" : "err";
  };

  /* ── Single row save ── */
  const handleSaveRow = async (row) => {
    setSaveStatus((s) => ({ ...s, [row._idx]: "saving" }));
    const status = await patchAllMatches(row);
    setSaveStatus((s) => ({ ...s, [row._idx]: status }));
    const count = row.dbMatches?.length || 1;
    if (status === "ok") toast.success(`${row.stockNo} — ${count} product(s) updated.`);
    else                 toast.error(`Failed to update ${row.stockNo}.`);
  };

  const handleUpdateAll = async () => {
    const toUpdate = displayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged));
    if (!toUpdate.length) { toast.info("No differences found — nothing to update."); return; }

    setSaving(true);
    const newStatus = { ...saveStatus };

    await Promise.all(
      toUpdate.map(async (row) => {
        newStatus[row._idx] = await patchAllMatches(row);
      })
    );

    setSaveStatus(newStatus);
    setSaving(false);

    const ok  = Object.values(newStatus).filter((v) => v === "ok").length;
    const err = Object.values(newStatus).filter((v) => v === "err").length;
    if (ok)  toast.success(`${ok} product(s) updated successfully.`);
    if (err) toast.error(`${err} product(s) failed to update.`);
  };

  /* ── filter pipeline ── */
  let displayed = rows;

  // 1. category tab
  if (activeCategory === "__unknown__") {
    displayed = displayed.filter(
      (r) => r.dbProduct && !isKnownCategory(r.dbProduct.category)
    );
  } else if (activeCategory !== "all") {
    displayed = displayed.filter(
      (r) => r.dbProduct?.category?.toLowerCase() === activeCategory.toLowerCase()
    );
  }

  // 2. search
  if (search.trim()) {
    const q = search.toLowerCase();
    displayed = displayed.filter(
      (r) =>
        String(r.stockNo).toLowerCase().includes(q) ||
        String(r.retailPrice).toLowerCase().includes(q) ||
        String(r.closingQty).toLowerCase().includes(q)
    );
  }

  // 3. changed-only toggle
  if (showChangedOnly) {
    displayed = displayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged));
  }

  // 3b. status chip filter
  if (statusFilter === "matched")  displayed = displayed.filter((r) => r.dbProduct);
  if (statusFilter === "changed")  displayed = displayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged));
  if (statusFilter === "nochange") displayed = displayed.filter((r) => r.dbProduct && !r.priceChanged && !r.qtyChanged);
  if (statusFilter === "notfound") displayed = displayed.filter((r) => !r.dbProduct);

  // 4. sort: Pending first → No Change → Not Found
  const sortRank = (r) => {
    if (r.dbProduct && (r.priceChanged || r.qtyChanged)) return 0;
    if (r.dbProduct && !r.priceChanged && !r.qtyChanged) return 1;
    return 2;
  };
  displayed = [...displayed].sort((a, b) => sortRank(a) - sortRank(b));

  /* ── summary counts (scoped to active category) ── */
  const scopedRows =
    activeCategory === "all"         ? rows :
    activeCategory === "__unknown__" ? rows.filter((r) => r.dbProduct && !isKnownCategory(r.dbProduct.category)) :
    rows.filter((r) => r.dbProduct?.category?.toLowerCase() === activeCategory.toLowerCase());

  const matchedCount  = scopedRows.filter((r) => r.dbProduct).length;
  const changedCount  = scopedRows.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged)).length;
  const noChangeCount = scopedRows.filter((r) => r.dbProduct && !r.priceChanged && !r.qtyChanged).length;
  const notFoundCount = scopedRows.filter((r) => !r.dbProduct).length;

  /* ── category tab count ── */
  const catCount = (val) => {
    if (val === "all")         return rows.length;
    if (val === "__unknown__") return rows.filter((r) => r.dbProduct && !isKnownCategory(r.dbProduct.category)).length;
    return rows.filter((r) => r.dbProduct?.category?.toLowerCase() === val.toLowerCase()).length;
  };

  return (
    <div className="eu-page">
      {/* Header */}
      <div className="eu-header">
        <h1 className="eu-title">Excel Upload</h1>
        {rows.length > 0 && <span className="eu-badge">{rows.length} rows loaded</span>}
      </div>

      {/* Drop Zone */}
      <div
        className={`eu-dropzone ${dragging ? "dragging" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv"
          style={{ display: "none" }} onChange={handleFile} />
        <div className="eu-dropzone-icon">&#128196;</div>
        <p className="eu-dropzone-text">
          {fileName ? fileName : "Click or drag & drop an Excel / CSV file here"}
        </p>
        <p className="eu-dropzone-hint">Supported: .xlsx, .xls, .csv</p>
        <p className="eu-dropzone-hint" style={{ marginTop: 4, color: "#0891b2" }}>
          Required columns: <strong>Stock No</strong>, <strong>Retail Price</strong>, <strong>Closing Bal.Qty</strong>
        </p>
      </div>

      {error && <div className="eu-error">{error}</div>}

      {rows.length > 0 && (
        <>
          {/* ── Category Tabs ── */}
          <div className="eu-cat-tabs-wrap">
            <div className="eu-cat-tabs">
              {CATEGORY_TABS.map((tab) => {
                const count = catCount(tab.value);
                return (
                  <button
                    key={tab.value}
                    className={`eu-cat-tab ${activeCategory === tab.value ? "active" : ""} ${tab.value === "__unknown__" ? "eu-cat-tab-warn" : ""}`}
                    onClick={() => { setActiveCategory(tab.value); setSearch(""); }}
                  >
                    {tab.label}
                    <span className="eu-cat-tab-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary chips */}
          <div className="eu-summary-row">
            <button className={`eu-chip chip-total ${statusFilter === "all" ? "chip-active" : ""}`}
              onClick={() => setStatusFilter("all")}>{scopedRows.length} Total</button>
            <button className={`eu-chip chip-match ${statusFilter === "matched" ? "chip-active" : ""}`}
              onClick={() => setStatusFilter("matched")}>{matchedCount} Matched</button>
            <button className={`eu-chip chip-changed ${statusFilter === "changed" ? "chip-active" : ""}`}
              onClick={() => setStatusFilter("changed")}>{changedCount} Have Changes</button>
            <button className={`eu-chip chip-nochange ${statusFilter === "nochange" ? "chip-active" : ""}`}
              onClick={() => setStatusFilter("nochange")}>{noChangeCount} No Change</button>
            <button className={`eu-chip chip-nomatch ${statusFilter === "notfound" ? "chip-active" : ""}`}
              onClick={() => setStatusFilter("notfound")}>{notFoundCount} Not Found</button>
          </div>

          {/* Toolbar */}
          <div className="eu-toolbar">
            <div className="eu-toolbar-left">
              <div className="eu-search-wrap">
                <span className="eu-search-icon">&#128269;</span>
                <input
                  type="text"
                  placeholder="Search by Stock No..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <label className="eu-toggle-label">
                <input
                  type="checkbox"
                  checked={showChangedOnly}
                  onChange={(e) => setShowChangedOnly(e.target.checked)}
                />
                Show changed only
              </label>
            </div>
            <div className="eu-toolbar-actions">
              <button className="eu-clear-btn" onClick={handleClear}>Clear &times;</button>
              <button
                className="eu-save-all-btn"
                onClick={handleUpdateAll}
                disabled={saving || displayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged)).length === 0}
              >
                {saving
                  ? "Updating..."
                  : `Update${activeCategory !== "all" ? " Category" : " All"} (${displayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged)).length} changed)`}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="eu-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Stock No</th>
                  <th>Category</th>
                  <th>Current Price</th>
                  <th>New Price</th>
                  <th>Current Qty</th>
                  <th>New Qty</th>
                  <th>Changes</th>
                  <th>Save</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length > 0 ? displayed.map((row) => {
                  const saved      = saveStatus[row._idx];
                  const hasChange  = row.priceChanged || row.qtyChanged;
                  const dbCat      = row.dbProduct?.category;
                  const knownCat   = isKnownCategory(dbCat);

                  return (
                    <tr key={row._idx} className={
                      saved === "ok"   ? "eu-row-saved"
                      : !row.dbProduct ? "eu-row-notfound"
                      : hasChange      ? "eu-row-changed"
                      : ""
                    }>
                      <td><div className="eu-serial">{row._idx + 1}</div></td>

                      <td>
                        {row.dbProduct?.images?.[0] ? (
                          <img
                            src={row.dbProduct.images[0].startsWith("http")
                              ? row.dbProduct.images[0]
                              : `${backend_url}${row.dbProduct.images[0]}`}
                            alt={row.dbProduct.name}
                            className="eu-row-img"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <div className="eu-row-img-empty">—</div>
                        )}
                      </td>

                      <td className="eu-id-cell">
                        {row.stockNo}
                        {row.dbMatches?.length > 1 && (
                          <span className="eu-multi-badge" title={`${row.dbMatches.length} products share this ID — all will be updated`}>
                            ×{row.dbMatches.length}
                          </span>
                        )}
                      </td>

                      <td>
                        {row.dbProduct ? (
                          knownCat
                            ? <span className="eu-cat-pill">{getCatLabel(dbCat)}</span>
                            : <span className="eu-cat-pill eu-cat-pill-unknown" title={`Raw DB value: "${dbCat}"`}>
                                ⚠ {dbCat}
                              </span>
                        ) : (
                          <span className="eu-na">—</span>
                        )}
                      </td>

                      {/* Price */}
                      <td className="eu-db-val">
                        {row.dbProduct ? fmtPrice(row.dbProduct.old_price) : <span className="eu-na">—</span>}
                      </td>
                      <td className={`eu-new-val ${row.priceChanged ? "eu-cell-diff" : ""}`}>
                        {fmtPrice(row.retailPrice)}
                        {row.priceChanged && <span className="eu-diff-dot" title="Changed" />}
                      </td>

                      {/* Qty */}
                      <td className="eu-db-val">
                        {row.dbProduct ? (row.dbProduct.quantity ?? "—") : <span className="eu-na">—</span>}
                      </td>
                      <td className={`eu-new-val ${row.qtyChanged ? "eu-cell-diff" : ""}`}>
                        {row.closingQty !== "" ? row.closingQty : "—"}
                        {row.qtyChanged && <span className="eu-diff-dot" title="Changed" />}
                      </td>

                      {/* Changes badge */}
                      <td>
                        {!row.dbProduct                               && <span className="eu-match-badge notfound">Not Found</span>}
                        {row.dbProduct && !hasChange                  && <span className="eu-match-badge nochange">No Change</span>}
                        {row.dbProduct && row.priceChanged && row.qtyChanged  && <span className="eu-match-badge both">Price + Qty</span>}
                        {row.dbProduct && row.priceChanged && !row.qtyChanged && <span className="eu-match-badge price">Price</span>}
                        {row.dbProduct && !row.priceChanged && row.qtyChanged && <span className="eu-match-badge qty">Qty</span>}
                      </td>

                      {/* Save status */}
                      <td>
                        {saved === "ok"  && <span className="eu-save-badge ok">✓ Updated</span>}
                        {saved === "err" && (
                          <button className="eu-row-save-btn eu-row-save-retry"
                            onClick={() => handleSaveRow(row)}>↺ Retry</button>
                        )}
                        {saved === "saving" && <span className="eu-save-badge pending">Saving…</span>}
                        {!saved && row.dbProduct && hasChange && (
                          <button className="eu-row-save-btn"
                            onClick={() => handleSaveRow(row)}>Save</button>
                        )}
                        {!saved && (!row.dbProduct || !hasChange) && <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="10">
                      <div className="eu-empty">
                        No records found{activeCategory !== "all" ? ` in ${CATEGORY_TABS.find(t => t.value === activeCategory)?.label}` : ""}.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ToastContainer />
    </div>
  );
};

export default ExcelUpload;
